/**
 * Creates a typed form-change tuple without exposing casts at call sites.
 */

export type ReactiveFormObject<TModel> = TModel extends object ? TModel : Record<string, never>;

export type ReactiveFormKey<TModel> = Extract<keyof ReactiveFormObject<TModel>, string>;

export type ReactiveFormDynamicKey = string & {
    readonly __reactiveFormDynamicKeyBrand?: unique symbol;
};

/**
 * Runtime value emitted by the reactive form builder.
 *
 * It keeps a typed alias for migrated components while the emitted tuple remains
 * intentionally permissive so legacy handlers that still receive broader tuple
 * shapes can compile during the incremental migration.
 */
export type ReactiveFormValue<TModel> = Partial<ReactiveFormObject<TModel>> & Record<string, unknown>;


/**
 * Name of the control that triggered the latest form emission.
 */
export type ReactiveFormChangedField<TModel> = ReactiveFormKey<TModel> | ReactiveFormDynamicKey;

/**
 * Backward-compatible tuple emitted by `(formChanges)`.
 *
 * Existing templates can continue using:
 * `(formChanges)="formChanges($event[0], $event[1])"`.
 *
 * The first tuple item is deliberately `any` while legacy form components are
 * still being migrated, because some old handlers are typed as `[object, string]`.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ReactiveFormChange = [any, string];


export type ReactiveFormFieldValue<
    TModel,
    TFieldName extends keyof ReactiveFormObject<TModel>
> = ReactiveFormObject<TModel>[TFieldName];
export function createReactiveFormChange<T>(
    value: ReactiveFormValue<T>,
    changedField: ReactiveFormChangedField<T>,
): ReactiveFormChange {
    return [value, String(changedField)];
}
